import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, QrCode, Download, X } from 'lucide-react';
import { promoterAPI, assignmentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatMoney, errMsg } from '../lib/roles';

/** Shared dashboard for independent promoters and SAC Events brand ambassadors. */
export default function PromoterDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [qr, setQr] = useState(null); // { url, title, code }
  const [payoutOpen, setPayoutOpen] = useState(false);

  const load = useCallback(() => {
    promoterAPI.dashboard().then(r => setData(r.data)).catch(e => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth?redirect=/promoter'); return; }
    load();
  }, [authLoading, user, load, navigate]);

  const respond = async (c, accept) => {
    try {
      await (accept ? assignmentsAPI.accept(c.assignment_id) : assignmentsAPI.decline(c.assignment_id));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const showQr = async (c) => {
    try {
      const r = await assignmentsAPI.qrBlob(c.assignment_id);
      setQr({ url: URL.createObjectURL(r.data), title: c.event?.title, code: c.tracking_code });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success('Link copied'));

  if (!data) return <div className="min-h-screen bg-[#050505] pt-24 text-center text-gray-400">Loading…</div>;
  const t = data.totals;

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-16 page-enter">
      <div className="max-w-5xl mx-auto px-4">
        <p className="text-xs font-bold tracking-widest text-red-500 uppercase">{data.is_ambassador ? 'SAC Events brand ambassador' : 'Promoter'}</p>
        <h1 className="text-2xl md:text-3xl font-black text-white uppercase mb-6">Promoter dashboard</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[['Assigned campaigns', t.assigned_campaigns], ['Link clicks', t.clicks], ['Tickets attributed', t.tickets], ['Earnings', formatMoney(t.earnings)]].map(([l, v]) => (
            <div key={l} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
              <p className="text-2xl font-black text-white">{v}</p>
              <p className="text-gray-500 text-xs">{l}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4 bg-[#0A0A0A] border border-red-600/50 rounded-lg p-4 mb-8">
          <div className="flex-1">
            <p className="text-[11px] font-black tracking-widest text-gray-500">AVAILABLE TO WITHDRAW</p>
            <p className="text-3xl font-black text-white">{formatMoney(data.balance.available)}</p>
            <p className="text-gray-500 text-xs">Earned {formatMoney(data.balance.earned)} · requested {formatMoney(data.balance.requested)}</p>
          </div>
          <button disabled={data.balance.available <= 0} onClick={() => setPayoutOpen(true)} className="btn-primary px-4 py-2 rounded text-sm font-bold disabled:opacity-50">
            Request payout
          </button>
        </div>

        <h2 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">MY CAMPAIGNS</h2>
        {data.campaigns.length === 0 && (
          <p className="text-gray-500 text-sm mb-6">No campaigns yet. Organisers can assign you to their events, and your tracking link will appear here.</p>
        )}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          {data.campaigns.map(c => (
            <div key={c.assignment_id} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
              <div className="flex gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold">{c.campaign_name || c.event?.title}</p>
                  {c.campaign_name && <Link to={`/events/${c.scope_id}`} className="text-gray-400 text-sm hover:text-red-400">{c.event?.title}</Link>}
                  <p className="text-gray-500 text-xs mt-1">
                    {c.status === 'pending' ? 'Invitation' : c.status === 'accepted' ? 'Active' : 'Ended'} ·{' '}
                    {c.from_sac_events ? 'Assigned by SAC Events' : `Assigned by ${c.assigned_by?.name || 'organiser'}`}
                    {c.deadline ? ` · deadline ${String(c.deadline).slice(0, 10)}` : ''}
                  </p>
                  {c.commission_type !== 'none' && (
                    <p className="text-gray-500 text-xs">Commission: {c.commission_type === 'percent' ? `${c.commission_value}% of sales` : `${formatMoney(c.commission_value)} per ticket`}</p>
                  )}
                </div>
                {c.rank?.position && (
                  <div className="text-center bg-[#111] rounded px-3 py-1 self-start">
                    <p className="text-red-500 font-black text-lg">#{c.rank.position}</p>
                    <p className="text-gray-500 text-[10px]">of {c.rank.of}</p>
                  </div>
                )}
              </div>

              {c.status === 'pending' ? (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => respond(c, false)} className="flex-1 border border-gray-700 text-gray-200 rounded py-2 text-sm font-bold">Decline</button>
                  <button onClick={() => respond(c, true)} className="flex-1 btn-primary rounded py-2 text-sm font-bold">Accept</button>
                </div>
              ) : (
                <>
                  {c.stats && (
                    <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-gray-800 text-center">
                      {[['Clicks', c.stats.clicks], ['Tickets', c.target_tickets ? `${c.stats.tickets}/${c.target_tickets}` : c.stats.tickets], ['Revenue', formatMoney(c.stats.revenue)], ['Earned', formatMoney(c.stats.earnings)]].map(([l, v]) => (
                        <div key={l}><p className="text-white font-black text-sm">{v}</p><p className="text-gray-500 text-[10px]">{l}</p></div>
                      ))}
                    </div>
                  )}
                  {c.target_tickets && c.stats && (
                    <div className="h-1.5 bg-[#111] rounded mt-2 overflow-hidden">
                      <div className="h-full bg-red-600" style={{ width: `${Math.min(100, (c.stats.tickets / c.target_tickets) * 100)}%` }} />
                    </div>
                  )}
                  {c.referral_link && c.status === 'accepted' && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2 bg-[#111] border border-gray-800 rounded px-3 py-2">
                        <span className="text-gray-200 text-xs truncate flex-1">{c.referral_link}</span>
                        <button onClick={() => copy(c.referral_link)} className="text-gray-400 hover:text-white" title="Copy"><Copy size={14} /></button>
                        <button onClick={() => showQr(c)} className="text-gray-400 hover:text-white" title="QR code"><QrCode size={14} /></button>
                      </div>
                    </div>
                  )}
                  {c.materials?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-gray-400 text-xs font-bold">Promotional materials</p>
                      {c.materials.map(m => (
                        <a key={m} href={m} target="_blank" rel="noopener noreferrer" className="block text-red-400 text-xs truncate hover:text-red-300">{m}</a>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <h2 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">MY SOCIAL PROFILES</h2>
        <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 mb-10">
          {Object.entries(data.social_links || {}).filter(([, v]) => v).length === 0 ? (
            <p className="text-gray-500 text-sm">Add your Instagram, TikTok and Facebook handles so organisers can see your reach. SAC Events never asks for your social-media passwords.</p>
          ) : Object.entries(data.social_links).filter(([, v]) => v).map(([k, v]) => (
            <p key={k} className="text-gray-300 text-sm"><span className="text-white font-semibold capitalize">{k}:</span> {v}</p>
          ))}
          <Link to="/account" className="text-red-400 text-sm font-bold mt-2 inline-block hover:text-red-300">Edit social profiles</Link>
        </div>

        {data.payouts?.length > 0 && (
          <>
            <h2 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">PAYOUT HISTORY</h2>
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg divide-y divide-gray-800">
              {data.payouts.map(p => (
                <div key={p.payout_id} className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-white font-semibold">{formatMoney(p.amount)}</span>
                  <span className="text-gray-400 capitalize">{p.status} · {String(p.created_at).slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {qr && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(qr.url); setQr(null); }}>
          <div className="bg-white rounded-xl p-6 text-center max-w-xs w-full" onClick={e => e.stopPropagation()}>
            <img src={qr.url} alt="Referral QR code" className="w-full" />
            <p className="text-black font-black mt-2">{qr.title}</p>
            <div className="flex gap-2 mt-3">
              <a href={qr.url} download={`sac-referral-${qr.code}.png`} className="flex-1 flex items-center justify-center gap-1 bg-black text-white rounded py-2 text-sm font-bold"><Download size={14} /> Download</a>
              <button onClick={() => { URL.revokeObjectURL(qr.url); setQr(null); }} className="flex-1 flex items-center justify-center gap-1 border border-gray-300 rounded py-2 text-sm font-bold text-black"><X size={14} /> Close</button>
            </div>
          </div>
        </div>
      )}

      {payoutOpen && <PayoutDialog max={data.balance.available} onClose={() => setPayoutOpen(false)} onDone={() => { setPayoutOpen(false); load(); }} />}
    </div>
  );
}

const inputCls = 'w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm';

function PayoutDialog({ max, onClose, onDone }) {
  const [f, setF] = useState({ amount: '', payoutMethod: 'bank', bankName: '', accountNumber: '', accountHolder: '', mobileMoneyNumber: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await promoterAPI.requestPayout({ ...f, amount: Number(f.amount), currency: 'EUR' });
      toast.success('Payout request sent to SAC Events');
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-[#0A0A0A] border border-gray-800 rounded-xl w-full max-w-md p-5 space-y-3">
        <h2 className="text-white font-black uppercase">Request payout</h2>
        <p className="text-gray-500 text-sm">Up to {formatMoney(max)}</p>
        <input className={inputCls} type="number" min="0.01" max={max} step="0.01" required value={f.amount} onChange={set('amount')} placeholder="Amount (EUR)" />
        <select className={inputCls} value={f.payoutMethod} onChange={set('payoutMethod')} style={{ background: '#111' }}>
          <option value="bank">Bank transfer</option>
          <option value="mobile_money">Mobile money</option>
        </select>
        {f.payoutMethod === 'bank' ? (
          <>
            <input className={inputCls} required value={f.bankName} onChange={set('bankName')} placeholder="Bank name" />
            <input className={inputCls} required value={f.accountNumber} onChange={set('accountNumber')} placeholder="Account number / IBAN" />
            <input className={inputCls} required value={f.accountHolder} onChange={set('accountHolder')} placeholder="Account holder" />
          </>
        ) : (
          <input className={inputCls} required value={f.mobileMoneyNumber} onChange={set('mobileMoneyNumber')} placeholder="Mobile money number" />
        )}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-gray-700 text-gray-200 rounded py-2.5 text-sm font-bold">Cancel</button>
          <button type="submit" disabled={busy} className="flex-1 btn-primary rounded py-2.5 text-sm font-bold disabled:opacity-60">{busy ? 'Sending…' : 'Submit'}</button>
        </div>
      </form>
    </div>
  );
}
